import {FEATURE_GATES_UNLIMITED} from '../../../../report/constants';
import {NAVIGATION_KEY_CODES} from '../../../../util/dom';
import {registerMPElement} from '../../../../util/register-element';

import {BuilderPaneBase} from '../../common-components/builder-pane-base/index';
import DateRange from '../../../date-range-picker/models/date-range';
import {DisplayContext} from '../../../date-range-picker/models/types';
import {DateRangeType} from '../../models/enums/builder-enums';

import template from './index.jade';
import css from './index.styl?inline';

export default registerMPElement(
  `cohorts-builder-time`,
  class CustomDateRangePane extends BuilderPaneBase {
    shouldUpdate(state) {
      return !!state.stagedCohort && !!state.stagedCohort.getStagedBehaviorDateRange(state.stagedClausePosition);
    }

    connectedCallback() {
      super.connectedCallback(...arguments);
      this.handleKeyPress = (ev) => {
        if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
          this.updateValueAndCommitStagedDateRange();
        }
      };

      window.addEventListener(`keydown`, this.handleKeyPress);
    }

    disconnectedCallback() {
      super.disconnectedCallback(...arguments);
      window.removeEventListener(`keydown`, this.handleKeyPress);
    }

    get config() {
      return {
        ...super.config,
        useShadowDom: true,
        css,
        template,
        helpers: {
          ...super.config.helpers,
          getDateRangeAttrs: () => {
            const maxHistoryDays = this.getMaxDataHistoryDays();
            let featureGatingAttrs = {};
            if (maxHistoryDays !== FEATURE_GATES_UNLIMITED) {
              featureGatingAttrs = {
                'show-upsell': maxHistoryDays < this.app.context.maxDataHistoryDaysUpgrade,
                'max-history-days': maxHistoryDays,
              };
            }

            return {
              'display-context': DisplayContext.InsightsTimePicker,
              'date-range': JSON.stringify(this.datePickerDateRange.getAttributes()),
              ...featureGatingAttrs,
            };
          },
          changedDateRange: (ev) => {
            const {dateRange} = ev.detail;
            const dateRangeUpdates = {
              customRangeType: dateRange.type,
              ...(dateRange.from ? {fromDate: dateRange.from} : {}),
              ...(dateRange.to ? {toDate: dateRange.to} : {}),
              ...(dateRange.window ? {value: dateRange.window.value, unit: dateRange.window.unit} : {}),
            };
            this.updateStagedBehaviorDateRange(dateRangeUpdates);
          },
          handleDateRangePickerInsert: (vnode) => {
            requestAnimationFrame(() => {
              switch (this.stagedDateRange.customRangeType) {
                case DateRangeType.Relative:
                  vnode.elm.selectRelativeInput();
                  break;
                case DateRangeType.On:
                case DateRangeType.Since:
                  vnode.elm.focusFromInput();
                  break;
                default:
                  break;
              }
            });
          },
          hasValidStagedDateRange: () => {
            const maxHistoryDays = this.getMaxDataHistoryDays();
            return (
              this.datePickerDateRange.isValid() &&
              (maxHistoryDays === FEATURE_GATES_UNLIMITED ||
                this.datePickerDateRange.isWithinMaxHistoryLimit(maxHistoryDays))
            );
          },
          clickedSelectDateRange: () => this.updateValueAndCommitStagedDateRange(),
          updateRenderedSize: () => this._updateRenderedSize(), // override throttling
        },
      };
    }

    commitStagedDateRange() {
      const {stagedCohort, stagedClausePosition} = this.state;
      const {customRangeType, fromDate, toDate, unit, value} = this.stagedDateRange;
      if (stagedCohort.hasValidStagedDateRange(stagedClausePosition)) {
        let range;
        switch (customRangeType) {
          case DateRangeType.Relative:
            range = {unit, value};
            break;
          case DateRangeType.Between:
            range = {from: fromDate, to: toDate};
            break;
          case DateRangeType.On:
            range = {from: fromDate, to: fromDate};
            break;
          case DateRangeType.Since:
            range = {from: fromDate};
            break;
          default:
            break;
        }

        this.dispatchBubbledEvent(`selected-custom-range`, range);
      } else {
        // select invalid inputs if clause is invalid
        switch (customRangeType) {
          case DateRangeType.Relative:
            this.dateRangePicker.selectRelativeInput();
            break;
          case DateRangeType.Between:
            this.dateRangePicker.selectToInput();
            break;
          default:
            this.dateRangePicker.selectFromInput();
            break;
        }
      }
    }

    updateValueAndCommitStagedDateRange() {
      // HACK: datetime inputs require a blur event to update their state and trigger a change event,
      // which then gets picked up by the model. this ensures that happens before we save changes
      this.focus();
      requestAnimationFrame(() => this.commitStagedDateRange());
    }

    updateStagedBehaviorDateRange(options) {
      const {stagedCohort, stagedClausePosition} = this.state;
      this.app.updateStagedCohort(stagedCohort.updateStagedBehaviorDateRange(options, stagedClausePosition));
    }

    get stagedDateRange() {
      const {stagedCohort, stagedClausePosition} = this.state;
      return stagedCohort.getStagedBehaviorDateRange(stagedClausePosition);
    }

    get datePickerDateRange() {
      const {customRangeType, fromDate, toDate, unit, value} = this.stagedDateRange;
      return new DateRange({
        type: customRangeType,
        from: fromDate,
        to: toDate,
        ...(fromDate
          ? {
              rolling: DateRange.isRelativeDate(fromDate),
            }
          : {}),
        window: {
          unit,
          value,
        },
      });
    }

    get dateRangePicker() {
      return this.el.querySelector(`date-range-picker`);
    }

    getMaxDataHistoryDays() {
      const {featureGates} = this.app.context;
      return featureGates && featureGates[`max_data_history_days`];
    }
  },
);
